import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  User, Mail, Trophy, Calendar, Crown, Zap, 
  CheckCircle, Star, RefreshCw, ArrowLeft, Settings,
  TrendingUp, Target, Award, Copy, Share2, Gift, School, Check,
  Code, ChevronDown, ChevronUp, XCircle, GraduationCap, Rocket, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { useMode } from "../context/ModeContext";
import { Blocks } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { MODE_META, modeLabel, normalizeMode } from "../lib/modes";

const API = "/api";

const TIER_LABELS = {
  free: "Free",
  student: "Student Plan",
  school: "Teacher Starter",
  teacher_plus: "Teacher Plus",
  teacher_pro: "Teacher Pro"
};

const PAID_TIERS = ["student", "teacher_plus", "teacher_pro"];

export default function Profile({ user, setUser, authChecked }) {
  const navigate = useNavigate();
  const { mode } = useMode();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [canRegenerate, setCanRegenerate] = useState(true);
  const [referralData, setReferralData] = useState(null);
  const [copyingCode, setCopyingCode] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [recentCode, setRecentCode] = useState([]);
  const [recentCodeExpanded, setRecentCodeExpanded] = useState(false);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    if (!user) {
      // Wait for the auth check before redirecting (direct load / hard refresh)
      if (authChecked) navigate("/");
      return;
    }
    fetchProfile();
    checkCanRegenerate();
    fetchReferralData();
    fetchSubscriptionInfo();
    fetchRecentCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authChecked, mode]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/user/profile?mode=${mode}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        toast.error("Failed to load profile");
      }
    } catch (error) {
      toast.error("Error loading profile");
    } finally {
      setLoading(false);
    }
  };

  const checkCanRegenerate = async () => {
    try {
      const response = await fetch(`${API}/auth/can-regenerate-nickname`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setCanRegenerate(data.can_regenerate);
      }
    } catch (error) {
      console.error("Error checking regenerate status:", error);
    }
  };

  const fetchReferralData = async () => {
    try {
      const response = await fetch(`${API}/auth/referral-code`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      }
    } catch (error) {
      console.error("Error fetching referral data:", error);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await fetch(`${API}/subscription/my-subscription`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionInfo(data);
      }
    } catch (error) {
      console.error("Error fetching subscription info:", error);
    }
  };

  const fetchRecentCode = async () => {
    try {
      const response = await fetch(`${API}/user/recent-code?mode=${mode}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setRecentCode(data.submissions || []);
      }
    } catch (error) {
      console.error("Error fetching recent code:", error);
    }
  };

  const handleCopyReferralCode = async () => {
    if (!referralData?.referral_code) return;
    
    setCopyingCode(true);
    try {
      await navigator.clipboard.writeText(referralData.referral_code);
      toast.success("Referral code copied!");
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = referralData.referral_code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("Referral code copied!");
    } finally {
      setCopyingCode(false);
    }
  };

  const handleCopyReferralLink = async () => {
    if (!referralData?.referral_url) return;
    
    try {
      await navigator.clipboard.writeText(referralData.referral_url);
      toast.success("Referral link copied!");
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleRegenerateNickname = async () => {
    if (!canRegenerate) {
      toast.error("You can only regenerate your nickname once per day");
      return;
    }
    
    setRegenerating(true);
    try {
      const response = await fetch(`${API}/auth/regenerate-nickname`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, nickname: data.nickname }));
        setUser(prev => ({ ...prev, nickname: data.nickname }));
        setCanRegenerate(false);
        toast.success(`New nickname: ${data.nickname}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to regenerate nickname");
      }
    } catch (error) {
      toast.error("Error regenerating nickname");
    } finally {
      setRegenerating(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await fetch(`${API}/subscription/cancel`, {
        method: "POST",
        credentials: "include"
      });
      const data = await response.json();
      if (response.ok) {
        const endDate = data.ends_at ? new Date(data.ends_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : "the end of your billing period";
        toast.success(`Subscription canceled. You'll keep access until ${endDate}.`);
        fetchSubscriptionInfo();
        fetchProfile();
      } else {
        toast.error(data.detail || "Failed to cancel subscription");
      }
    } catch (error) {
      toast.error("Error canceling subscription");
    } finally {
      setCancelling(false);
    }
  };

  const handleResumeSubscription = async () => {
    setResuming(true);
    try {
      const response = await fetch(`${API}/subscription/reactivate`, {
        method: "POST",
        credentials: "include"
      });
      const data = await response.json();
      if (response.ok) {
        toast.success("Subscription resumed! Your plan will continue as normal.");
        fetchSubscriptionInfo();
        fetchProfile();
      } else {
        toast.error(data.detail || "Failed to resume subscription");
      }
    } catch (error) {
      toast.error("Error resuming subscription");
    } finally {
      setResuming(false);
    }
  };

  const getSubscriptionBadge = (tier, isSchoolMember) => {
    // School students should show School Member badge even if tier is "free" or null/undefined
    if (isSchoolMember && (!tier || tier === "free")) {
      return (
        <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white gap-1">
          <School className="w-3 h-3" />
          School Member
        </Badge>
      );
    }
    
    switch (tier) {
      case "student":
        return (
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white gap-1">
            <Crown className="w-3 h-3" />
            Student Plan
          </Badge>
        );
      case "school":
        return (
          <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white gap-1">
            <Award className="w-3 h-3" />
            Teacher Starter
          </Badge>
        );
      case "teacher_plus":
        return (
          <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white gap-1">
            <GraduationCap className="w-3 h-3" />
            Teacher Plus
          </Badge>
        );
      case "teacher_pro":
        return (
          <Badge className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white gap-1">
            <Rocket className="w-3 h-3" />
            Teacher Pro
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="gap-1">
            <User className="w-3 h-3" />
            Free Plan
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Could not load profile</p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-6 gap-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        {/* Profile Header */}
        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {profile.picture ? (
                <img 
                  src={profile.picture} 
                  alt={profile.name} 
                  className="w-20 h-20 rounded-2xl"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <User className="w-10 h-10 text-primary" />
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold truncate" data-testid="profile-nickname">
                  {profile.nickname}
                </h1>
                <button
                  onClick={handleRegenerateNickname}
                  disabled={regenerating || !canRegenerate}
                  className={`p-1.5 rounded-lg transition-colors ${
                    canRegenerate 
                      ? "hover:bg-muted text-muted-foreground hover:text-primary" 
                      : "text-muted-foreground/50 cursor-not-allowed"
                  }`}
                  title={canRegenerate ? "Regenerate nickname" : "Already regenerated today"}
                  data-testid="regenerate-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} />
                </button>
              </div>

              {profile.name && (
                <p className="text-muted-foreground mb-2">{profile.name}</p>
              )}

              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                <Mail className="w-4 h-4" />
                <span data-testid="profile-email">{profile.email}</span>
              </div>

              <div data-testid="subscription-tier-badge" className="inline-flex">
                {getSubscriptionBadge(profile.subscription_tier, subscriptionInfo?.schools?.length > 0)}
              </div>
            </div>
          </div>
        </div>

        {/* School Membership Banner - Show for users who are part of school(s) */}
        {subscriptionInfo?.schools && subscriptionInfo.schools.length > 0 && (
          <div className="bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-teal-500/10 rounded-2xl border border-blue-500/30 p-6 mb-6" data-testid="school-membership-banner">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-blue-500/20 shrink-0">
                <School className="w-6 h-6 text-blue-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-foreground mb-1">
                  Congratulations! You're registered as part of {subscriptionInfo.schools.length === 1 ? 'a school' : 'schools'}:
                </h3>
                <div className="mb-4">
                  {subscriptionInfo.schools.map((school, index) => (
                    <span key={school.school_id} className="font-semibold text-blue-600 dark:text-blue-400">
                      {school.school_name}{index < subscriptionInfo.schools.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  As a school member, you have access to these benefits:
                </p>
                
                <div className="grid sm:grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-green-500" />
                    </div>
                    <span><strong>5 bonus challenges</strong> per day</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-green-500" />
                    </div>
                    <span><strong>Show hints</strong> for all questions</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-green-500" />
                    </div>
                    <span><strong>Create your own questions</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-green-500" />
                    </div>
                    <span><strong>School leaderboard</strong> rankings</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid (per coding track) */}
        <div className="flex items-center gap-2 mb-2" data-testid="profile-mode-badge">
          <Badge variant="outline" className={`gap-1 ${mode === "python" ? "text-primary border-primary" : MODE_META[normalizeMode(mode)].badge}`}>
            {(() => { const I = MODE_META[normalizeMode(mode)].icon; return <I className="w-3 h-3" />; })()}
            {modeLabel(mode)} track
          </Badge>
          <span className="text-xs text-muted-foreground">Switch tracks with the Junior | Blocks | Python control in the header.</span>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Total Score */}
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Trophy className="w-4 h-4 text-amber-500" />
              <span className="text-sm">Total Score</span>
            </div>
            <p className="text-2xl font-bold" data-testid="total-score">{profile.total_score ?? 0}</p>
          </div>

          {/* Weekly Score */}
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className="text-sm">This Week</span>
            </div>
            <p className="text-2xl font-bold" data-testid="weekly-score">{profile.weekly_score ?? 0}</p>
          </div>

          {/* Total Answered */}
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <CheckCircle className="w-4 h-4 text-cyan-500" />
              <span className="text-sm">Total Solved</span>
            </div>
            <p className="text-2xl font-bold" data-testid="total-answered">{profile.total_answered ?? 0}</p>
          </div>

          {/* Weekly Answered */}
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Target className="w-4 h-4 text-purple-500" />
              <span className="text-sm">Solved This Week</span>
            </div>
            <p className="text-2xl font-bold" data-testid="weekly-answered">{profile.weekly_answered ?? 0}</p>
          </div>
        </div>

        {/* Current Streak */}
        {profile.streak > 0 && (
          <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-xl border border-orange-500/20 p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <Zap className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Streak</p>
                <p className="text-xl font-bold">{profile.streak} days</p>
              </div>
            </div>
          </div>
        )}

        {/* Referral Section - Only for Student tier */}
        {referralData?.eligible && referralData?.referral_code && (
          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl border border-purple-500/20 p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Gift className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <h2 className="font-semibold text-lg">Invite Friends & Save</h2>
                <p className="text-sm text-muted-foreground">You both get 50% off - you on your next month, your friend on their first</p>
              </div>
            </div>

            {/* Referral Code Display */}
            <div className="bg-background/50 rounded-xl p-4 mb-4">
              <p className="text-xs text-muted-foreground mb-2">Your Referral Code</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-2xl font-mono font-bold text-primary tracking-wider">
                  {referralData.referral_code}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyReferralCode}
                  disabled={copyingCode}
                  className="gap-1"
                  data-testid="copy-referral-code-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </Button>
              </div>
            </div>

            {/* Referral Link */}
            <div className="bg-background/50 rounded-xl p-4 mb-4">
              <p className="text-xs text-muted-foreground mb-2">Share Link</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm font-mono text-muted-foreground truncate">
                  {referralData.referral_url}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyReferralLink}
                  className="gap-1"
                  data-testid="copy-referral-link-btn"
                >
                  <Share2 className="w-4 h-4" />
                  Copy
                </Button>
              </div>
            </div>

            {/* Discounted Months Counter */}
            {referralData.discounted_months_remaining > 0 && (
              <div className="bg-green-500/10 rounded-xl p-4 mb-4 border border-green-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Discounted Months Available</p>
                    <p className="text-2xl font-bold text-green-600">
                      {referralData.discounted_months_remaining}
                    </p>
                  </div>
                  <Badge className="bg-green-500/20 text-green-600 border-green-500/30">
                    50% Off Each
                  </Badge>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="space-y-2 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">How it works:</p>
              <ol className="list-decimal list-inside space-y-1 ml-2" data-testid="referral-how-it-works">
                <li>Share your referral code or link with friends</li>
                <li>They enter it when subscribing to the Student plan and get <span className="text-primary font-medium">50% off their first month</span></li>
                <li>Once their first payment goes through, you get <span className="text-primary font-medium">50% off</span> your next billing cycle</li>
                <li>Discounts stack - invite more friends, save more!</li>
              </ol>
            </div>
          </div>
        )}

        {/* Subscription Section */}
        <div className="bg-card rounded-2xl border border-border p-6">
          <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Subscription
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-border">
              <span className="text-muted-foreground">Current Plan</span>
              <span className="font-medium">
                {subscriptionInfo?.schools?.length > 0 && profile.subscription_tier === "free" 
                  ? "School Member" 
                  : (TIER_LABELS[profile.subscription_tier] || profile.subscription_tier || "Free")}
              </span>
            </div>

            {profile.subscription_status && (
              <div className="flex items-center justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">Status</span>
                <Badge variant={profile.subscription_status === "active" ? "default" : "secondary"}>
                  {profile.subscription_status}
                </Badge>
              </div>
            )}

            {profile.subscription_started_at && (
              <div className="flex items-center justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">Member Since</span>
                <span className="font-medium">
                  {new Date(profile.subscription_started_at).toLocaleDateString()}
                </span>
              </div>
            )}

            {profile.school_name && (
              <div className="flex items-center justify-between py-3 border-b border-border">
                <span className="text-muted-foreground">School</span>
                <span className="font-medium">{profile.school_name}</span>
              </div>
            )}

            {/* Pending Cancellation Notice */}
            {subscriptionInfo?.cancel_at_period_end && (
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20" data-testid="cancellation-pending-notice">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                      Your plan is set to cancel
                      {subscriptionInfo.subscription_ends_at
                        ? ` on ${new Date(subscriptionInfo.subscription_ends_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`
                        : " at the end of your billing period"}.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      You'll keep full access until then.
                      {["teacher_plus", "teacher_pro"].includes(profile.subscription_tier) &&
                        " After that, your school moves to the free Teacher Starter tier (3 live players, 10 student slots) — existing students are kept."}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 gap-2"
                      onClick={handleResumeSubscription}
                      disabled={resuming}
                      data-testid="resume-subscription-btn"
                    >
                      {resuming ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Resume Subscription
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Action Button - Don't show upgrade for school members */}
            <div className="pt-2 space-y-3">
              {profile.subscription_tier === "free" && !(subscriptionInfo?.schools?.length > 0) ? (
                <Link to="/subscription" className="block">
                  <Button className="w-full gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90" data-testid="upgrade-btn">
                    <Crown className="w-4 h-4" />
                    Upgrade to Student Plan
                  </Button>
                </Link>
              ) : (
                <Link to="/subscription" className="block">
                  <Button variant="outline" className="w-full gap-2" data-testid="manage-subscription-btn">
                    <Settings className="w-4 h-4" />
                    {subscriptionInfo?.schools?.length > 0 && !PAID_TIERS.includes(profile.subscription_tier) ? "View Plans" : "Manage Subscription"}
                  </Button>
                </Link>
              )}

              {/* Cancel Subscription - paid plans with an active Stripe subscription */}
              {PAID_TIERS.includes(profile.subscription_tier) &&
               subscriptionInfo?.subscription_status === "active" &&
               !subscriptionInfo?.cancel_at_period_end && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full gap-2 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                      disabled={cancelling}
                      data-testid="cancel-subscription-btn"
                    >
                      {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                      Cancel Subscription
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="cancel-subscription-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel your {TIER_LABELS[profile.subscription_tier]} subscription?</AlertDialogTitle>
                      <AlertDialogDescription asChild>
                        <div className="space-y-2">
                          <p>Your plan stays active until the end of the current billing period — no further charges after that.</p>
                          {["teacher_plus", "teacher_pro"].includes(profile.subscription_tier) ? (
                            <p>
                              Afterwards your school moves to the free <strong>Teacher Starter</strong> tier:
                              3 live drill players and 10 tracked student slots. Your existing students are kept,
                              but you won't be able to add new ones while over the limit.
                            </p>
                          ) : (
                            <p>Afterwards you'll move to the Free plan (3 challenges/day, no hints).</p>
                          )}
                          <p>You can resume any time before the period ends.</p>
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="cancel-subscription-keep-btn">Keep My Plan</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleCancelSubscription}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        data-testid="cancel-subscription-confirm-btn"
                      >
                        Cancel Subscription
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </div>

        {/* Recent Code Section */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden mb-6">
          <button
            onClick={() => setRecentCodeExpanded(!recentCodeExpanded)}
            className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
            data-testid="recent-code-toggle"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-purple-500/10">
                <Code className="w-5 h-5 text-purple-500" />
              </div>
              <div className="text-left">
                <h2 className="font-semibold">Recent Code</h2>
                <p className="text-sm text-muted-foreground">
                  {recentCode.length > 0 
                    ? `${recentCode.length} recent submission${recentCode.length !== 1 ? 's' : ''}`
                    : 'No submissions yet'}
                </p>
              </div>
            </div>
            {recentCodeExpanded ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {recentCodeExpanded && (
            <div className="border-t border-border">
              {recentCode.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <Code className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No code submissions yet</p>
                  <p className="text-sm mt-1">Start solving problems to see your code history</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {recentCode.map((submission, index) => (
                    <div key={`${submission.question_id}-${index}`} className="p-4">
                      <button
                        onClick={() => setExpandedSubmission(
                          expandedSubmission === submission.question_id ? null : submission.question_id
                        )}
                        className="w-full text-left"
                        data-testid={`submission-${submission.question_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium truncate pr-4">
                            {submission.question_title}
                          </h3>
                          <Badge 
                            variant={submission.correct ? "default" : "secondary"}
                            className={submission.correct 
                              ? "bg-green-500/10 text-green-600 border-green-500/20" 
                              : "bg-amber-500/10 text-amber-600 border-amber-500/20"
                            }
                          >
                            {submission.passed}/{submission.total}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>
                            {new Date(submission.submitted_at).toLocaleDateString('en-AU', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {submission.correct && (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-3 h-3" /> Solved
                            </span>
                          )}
                        </div>
                      </button>

                      {expandedSubmission === submission.question_id && (
                        <div className="mt-3 rounded-lg bg-muted/50 overflow-hidden">
                          <div className="p-2 bg-muted/80 text-xs text-muted-foreground font-medium flex items-center justify-between">
                            <span>Python</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(submission.code);
                                toast.success("Code copied!");
                              }}
                              className="flex items-center gap-1 hover:text-foreground transition-colors"
                            >
                              <Copy className="w-3 h-3" /> Copy
                            </button>
                          </div>
                          <pre className="p-3 text-sm font-mono overflow-x-auto max-h-[300px] overflow-y-auto">
                            <code>{submission.code}</code>
                          </pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Account Info */}
        {profile.created_at && (
          <p className="text-center text-xs text-muted-foreground mt-6">
            Account created {new Date(profile.created_at).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
}
